import React from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { Brain, BookOpenText, Sparkles, GraduationCap, Globe2, ArrowRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import LanguageToggle from "../components/LanguageToggle";
import Brand from "../components/Brand";

export default function Landing() {
  const { t, isRTL } = useLanguage();
  const { user } = useAuth();

  // Device enrollment deep link: /?code=XXXX-XXXX-XXXX survives through OAuth.
  const enrollCode = (() => {
    try {
      return new URLSearchParams(window.location.search).get("code") || "";
    } catch { return ""; }
  })();

  const handleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const codeSuffix = enrollCode ? `?code=${encodeURIComponent(enrollCode)}` : "";
    const redirectUrl = window.location.origin + "/auth-callback" + codeSuffix;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const goDash = () => (window.location.href = user?.role === "admin" ? "/admin" : "/dashboard");

  return (
    <div className="min-h-screen bg-bone relative overflow-hidden">
      {/* Decorative shapes */}
      <div aria-hidden className="pointer-events-none absolute -top-40 end-[-10rem] w-[40rem] h-[40rem] rounded-full bg-sage/10 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute bottom-[-15rem] start-[-10rem] w-[35rem] h-[35rem] rounded-full bg-terracotta/10 blur-3xl" />

      <nav className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 flex items-center justify-between">
        <Brand size="md" linkTo="/" />
      </nav>

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-border px-3 py-1 text-xs uppercase tracking-[0.18em] text-sage font-medium mb-6">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
              Egyptian German Nursing School · 2026
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-foreground">
              {t("heroTitle")}
            </h1>
            <div className="mt-4 font-display text-lg sm:text-xl text-terracotta font-medium tracking-tight" data-testid="hero-tagline">
              {t("heroTagline")}
            </div>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed">
              {t("heroSub")}
            </p>

            {enrollCode && (
              <div
                className="mt-6 inline-flex items-start gap-3 bg-sage/5 border border-sage/30 rounded-2xl px-4 py-3 max-w-xl"
                data-testid="enroll-banner"
              >
                <div className="w-8 h-8 rounded-lg bg-sage/15 text-sage flex items-center justify-center shrink-0 mt-0.5">
                  <Sparkles className="w-4 h-4" strokeWidth={1.8} />
                </div>
                <div className="text-sm leading-snug">
                  <div className="font-medium text-sage">
                    {isRTL ? "جاهز لتسجيل هذا الجهاز" : "Ready to enroll this device"}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {isRTL ? "سيتم استخدام رمز التفعيل" : "Using activation code"}
                    {" "}
                    <span className="font-mono font-semibold text-foreground">{enrollCode}</span>
                    {". "}
                    {isRTL ? "سجّل الدخول أدناه للمتابعة." : "Sign in below to continue."}
                  </div>
                </div>
              </div>
            )}

            <div className="mt-10 flex flex-wrap items-center gap-3">
              {user ? (
                <button
                  data-testid="continue-cta"
                  onClick={goDash}
                  className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-white font-medium px-6 py-3.5 rounded-xl transition-all hover:-translate-y-0.5 shadow-sm hover:shadow-lg"
                >
                  <GraduationCap className="w-5 h-5" strokeWidth={1.8} />
                  {t("continue")}
                  <ArrowRight className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} strokeWidth={2} />
                </button>
              ) : (
                <button
                  data-testid="google-signin-button"
                  onClick={handleSignIn}
                  className="inline-flex items-center gap-3 bg-sage hover:bg-sage-dark text-white font-medium px-6 py-3.5 rounded-xl transition-all hover:-translate-y-0.5 shadow-sm hover:shadow-lg"
                >
                  <GoogleIcon />
                  {t("signIn")}
                  <ArrowRight className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} strokeWidth={2} />
                </button>
              )}
              <div className="inline-flex items-center gap-1.5 ps-4 pe-1 py-1 rounded-full bg-white border border-border text-xs text-muted-foreground">
                <Globe2 className="w-3.5 h-3.5 text-sage" strokeWidth={2} />
                English · العربية
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-sage text-white text-[10px] font-bold ms-1">AI</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-sage/20 via-transparent to-terracotta/20 rounded-[2rem] blur-2xl" aria-hidden />
              <img
                src="/brand/egns-banner.jpg"
                alt="Egyptian German Nursing School — The right start begins here"
                data-testid="egns-banner"
                className="relative rounded-[2rem] shadow-2xl border border-white w-full object-contain bg-white"
              />
              <div className="absolute -bottom-6 -start-6 bg-white rounded-2xl shadow-xl border border-border px-5 py-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-sage/10 flex items-center justify-center">
                  <BookOpenText className="w-5 h-5 text-sage" strokeWidth={1.8} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{t("chapter")} 1</div>
                  <div className="font-medium text-sm">Foundation of Nursing</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="mt-28 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <Feature icon={BookOpenText} title={t("featureLessons")} sub={t("featureLessonsSub")} />
          <Feature icon={Sparkles} title={t("featureFlash")} sub={t("featureFlashSub")} accent />
          <Feature icon={Brain} title={t("featureQuiz")} sub={t("featureQuizSub")} />
          <Feature icon={Globe2} title={t("featureLang")} sub={t("featureLangSub")} accent />
        </div>
      </main>

      <LanguageToggle />
    </div>
  );
}

function Feature({ icon: Icon, title, sub, accent }) {
  return (
    <div className="group rounded-2xl bg-white border border-border p-6 transition-all hover:-translate-y-1 hover:shadow-md">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${accent ? "bg-terracotta/10 text-terracotta" : "bg-sage/10 text-sage"}`}>
        <Icon className="w-5 h-5" strokeWidth={1.8} />
      </div>
      <div className="font-display text-lg font-semibold">{title}</div>
      <div className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{sub}</div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <path fill="#fff" d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.566 2.684-3.874 2.684-6.614z"/>
      <path fill="#fff" opacity="0.85" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.838.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#fff" opacity="0.7" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
      <path fill="#fff" opacity="0.55" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
    </svg>
  );
}
